import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { BrainCircuit, ClipboardList, Compass, Heart, Route, Plane, ChevronRight, ChevronDown, Globe, MapPin, BarChart3, ArrowRight, User, CheckCircle2, X } from "lucide-react";
import { SEO } from "@/components/SEO";

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const justSignedUp = (location.state as any)?.justSignedUp === true;
  const signedUpEmail = (location.state as any)?.email as string | undefined;
  const [showWelcome, setShowWelcome] = useState(justSignedUp);

  useEffect(() => {
    if (justSignedUp) {
      // Clear navigation state so banner doesn't reappear on back/refresh
      window.history.replaceState({}, '');
    }
  }, [justSignedUp]);

  return (
    <div className="relative overflow-hidden">
      {showWelcome && (
        <div className="bg-highlight text-white">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium truncate">
                Welcome to TripVerse{signedUpEmail ? `, ${signedUpEmail}` : ''}! You're signed in — your saves and trips will now sync to your account.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowWelcome(false)}
              aria-label="Dismiss welcome message"
              className="flex-shrink-0 rounded-full p-1 hover:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {/* Hero Section — Bold navy panel */}
        <SEO title="AI-Powered Travel Destination Finder" description="Take a quick quiz and discover your perfect travel destination with real-time pricing." />
      <section className="relative bg-primary overflow-hidden isolate">
        {/* Decorative geometric shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large accent circle — top right */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-accent/10 blur-3xl" />
          {/* Small accent dot — bottom left */}
          <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-accent/8 blur-2xl" />
          {/* Grid lines */}
          {/* Accent stripe */}
          <div className="absolute top-0 left-0 w-1.5 h-full bg-accent/40" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/15 text-accent-foreground border border-accent/30 backdrop-blur-sm">
              <BrainCircuit className="h-4 w-4 rotate-90" />
              <span className="text-xs sm:text-sm font-semibold tracking-wide uppercase text-white/90">AI-Powered Travel Decisions</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-6xl md:text-7xl leading-[1.08]" style={{ letterSpacing: '-0.03em' }}>
              <span className="font-normal text-primary-foreground">Let's Decide Your Next</span>
              <br />
              <span className="font-black text-5xl sm:text-7xl md:text-8xl tracking-tight" style={{ color: 'hsl(215 90% 65%)', textShadow: '0 0 40px hsl(215 90% 48% / 0.5)' }}>
                Destination
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl md:text-2xl text-primary-foreground/70 max-w-2xl mx-auto font-light">
              Skip the overwhelm. Cut the endless scrolling. Find your next trip in 2 minutes.
            </p>

            {/* CTA */}
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                variant="hero" 
                size="lg"
                onClick={() => navigate('/quiz')}
                className="text-base sm:text-lg px-10 sm:px-14 py-5 sm:py-7 h-auto group"
              >
                Start Your Journey
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Trust line */}
            <p className="text-primary-foreground/80 text-sm font-medium tracking-wide">
              No account needed. 2-minute quiz, instant results.
            </p>
          </div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
            <path d="M0 40C240 80 480 0 720 40C960 80 1200 0 1440 40V80H0V40Z" fill="hsl(var(--background) / 0.35)" />
          </svg>
        </div>
      </section>

      {/* Journey Flow — How It Works */}
      <section
        className="py-20 sm:py-28 relative"
        style={{ contentVisibility: "auto", containIntrinsicSize: "1000px" }}
      >
        <div className="absolute inset-0 -z-10 pointer-events-none bg-[radial-gradient(ellipse_at_15%_18%,hsl(var(--accent)/0.28),transparent_55%),radial-gradient(ellipse_at_82%_48%,hsl(var(--highlight)/0.30),transparent_58%),linear-gradient(180deg,transparent,hsl(var(--background)/0.12))]" />
        {/* Clean top + bottom hairlines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-border" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />

        <div className="container mx-auto px-4 sm:px-6">
          {/* Use case framing */}
          <div className="max-w-5xl mx-auto mb-20 sm:mb-28">
            <div className="relative rounded-3xl bg-card border border-border p-8 sm:p-12 md:p-16 overflow-hidden shadow-soft">
              {/* Accent stripe */}
              <div className="absolute top-0 left-0 w-1.5 h-full bg-accent" />

              <div className="relative grid md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-start">
                {/* Icon badge */}
                <div className="flex md:block justify-center">
                  <div className="relative">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-accent/15 border border-accent/30 flex items-center justify-center relative overflow-visible">
                      <User className="h-10 w-10 sm:h-12 sm:w-12 text-accent translate-y-1" strokeWidth={1.75} />
                      {/* Question marks just above head */}
                      <span className="absolute top-1 left-1/2 -translate-x-[210%] text-accent text-base sm:text-lg font-bold leading-none rotate-[-15deg] select-none">?</span>
                      <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 text-accent text-lg sm:text-xl font-bold leading-none select-none">?</span>
                      <span className="absolute top-1 left-1/2 translate-x-[110%] text-accent/70 text-sm sm:text-base font-bold leading-none rotate-[15deg] select-none">?</span>
                    </div>
                  </div>
                </div>

                <div className="text-center md:text-left space-y-5">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-highlight/10 border border-highlight/30 text-highlight text-xs font-semibold tracking-widest uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-highlight animate-pulse" />
                    Built For The Undecided
                  </span>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
                    Ready to travel, but{" "}
                    <span className="text-highlight">stuck deciding where?</span>
                  </h2>
                  <p className="text-muted-foreground text-base sm:text-lg font-light leading-relaxed">
                    You're not short on options, you're drowning in them. <span className="text-foreground font-medium">TripVerse exists to make travel decisions simple</span>, for your next trip and every trip after. Answer a few quick questions and <span className="text-foreground font-medium">get a clear shortlist built around exactly what you want</span>, with real destinations, real prices, and real plans.
                  </p>

                  {/* Pain → Solution chips */}
                  <div className="flex flex-wrap gap-2.5 justify-center md:justify-start pt-3">
                    {["Beat decision paralysis", "No more endless tabs", "No more blog rabbit holes", "No more group chat debates"].map((chip) => (
                      <span key={chip} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent/10 border-2 border-accent/40 text-sm sm:text-base font-semibold text-foreground shadow-sm hover:bg-accent/15 hover:border-accent/60 hover:scale-105 transition-all duration-200">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-accent text-accent-foreground text-xs font-black shadow-md">✓</span>
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative rounded-3xl bg-card border border-border p-8 sm:p-12 md:p-16 overflow-hidden shadow-soft">
            {/* Decorative accent corner */}
            <div className="absolute top-0 right-0 w-1.5 h-full bg-accent/60" />

            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-highlight/10 border border-highlight/30 text-highlight text-xs font-semibold tracking-widest uppercase mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-highlight animate-pulse" />
                The Process
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
                From Stuck to <span className="text-highlight">Sorted</span>
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg font-light max-w-xl mx-auto">Five guided steps that replace hours of second-guessing with one confident decision.</p>
            </div>

          {/* Desktop: Horizontal Flow */}
          <div className="hidden lg:flex items-start justify-center gap-4 max-w-6xl mx-auto">
            {[
              { icon: ClipboardList, title: "Tell Us Your Vibe", desc: "A 2-minute quiz captures your style, budget, and must-haves. No fluff.", step: 1 },
              { icon: Compass, title: "Get Matched Instantly", desc: "AI cuts through endless options to surface destinations built around you, with live prices.", step: 2 },
              { icon: Heart, title: "Shortlist with Confidence", desc: "Save the picks that feel right and swap out the ones that don't, all in one place.", step: 3 },
              { icon: Route, title: "See the Trip, Day by Day", desc: "Generate a tailored itinerary so you know exactly what your trip will look like.", step: 4 },
              { icon: Plane, title: "Lock It In", desc: "Head to trusted flight and hotel partners and turn your plan into a real trip.", step: 5 },
            ].map((item, index) => (
              <div key={item.step} className="flex items-center">
                <div className="flex flex-col items-center text-center w-48 group p-5 rounded-2xl bg-card border border-border hover:border-accent/50 hover:shadow-soft hover:-translate-y-1 transition-all duration-300">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center border border-accent/20 group-hover:bg-accent/15 group-hover:scale-105 group-hover:border-accent/50 transition-all duration-300">
                      <item.icon className="h-8 w-8 text-accent" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-highlight text-highlight-foreground text-sm font-bold flex items-center justify-center shadow-md ring-2 ring-background">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="font-bold mt-4 text-base">{item.title}</h3>
                  <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{item.desc}</p>
                </div>
                {index < 4 && (
                  <div className="flex items-center mx-2 flex-shrink-0" style={{ marginTop: '-1rem' }}>
                    <div className="w-8 h-0.5 bg-accent/50 rounded-full" />
                    <ChevronRight className="h-7 w-7 text-accent -ml-2" strokeWidth={2.5} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile/Tablet: Vertical Flow */}
          <div className="lg:hidden flex flex-col items-center gap-6 max-w-md mx-auto">
            {[
              { icon: ClipboardList, title: "Tell Us Your Vibe", desc: "A 2-minute quiz captures your style, budget, and must-haves. No fluff.", step: 1 },
              { icon: Compass, title: "Get Matched Instantly", desc: "AI cuts through endless options to surface destinations built around you, with live prices.", step: 2 },
              { icon: Heart, title: "Shortlist with Confidence", desc: "Save the picks that feel right and swap out the ones that don't, all in one place.", step: 3 },
              { icon: Route, title: "See the Trip, Day by Day", desc: "Generate a tailored itinerary so you know exactly what your trip will look like.", step: 4 },
              { icon: Plane, title: "Lock It In", desc: "Head to trusted flight and hotel partners and turn your plan into a real trip.", step: 5 },
            ].map((item, index) => (
              <div key={item.step} className="flex flex-col items-center w-full">
                <div className="flex items-center gap-5 p-5 rounded-2xl bg-card border border-border w-full hover:border-accent/50 hover:shadow-soft transition-all duration-300">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center border border-accent/20">
                      <item.icon className="h-7 w-7 text-accent" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-highlight text-highlight-foreground text-sm font-bold flex items-center justify-center ring-2 ring-background">
                      {item.step}
                    </span>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <h3 className="font-bold text-base">{item.title}</h3>
                    <p className="text-muted-foreground text-sm mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                {index < 4 && (
                  <div className="flex flex-col items-center py-2">
                    <div className="w-0.5 h-4 bg-accent/50 rounded-full" />
                    <ChevronDown className="h-6 w-6 text-accent -mt-1" strokeWidth={2.5} />
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* Track Your Adventures Section — Clean muted panel */}
      <section
        className="py-16 sm:py-24 relative border-t border-border"
        style={{ contentVisibility: "auto", containIntrinsicSize: "700px" }}
      >
        <div className="absolute inset-0 -z-10 pointer-events-none bg-[radial-gradient(ellipse_at_20%_35%,hsl(var(--highlight)/0.30),transparent_55%),radial-gradient(ellipse_at_88%_65%,hsl(var(--accent)/0.28),transparent_58%)]" />

        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-3xl bg-card border border-border p-8 sm:p-12 md:p-16 overflow-hidden shadow-soft">
              {/* Decorative accent stripe */}
              <div className="absolute top-0 left-0 w-1.5 h-full bg-accent/60" />

              <div className="text-center space-y-4 mb-12 sm:mb-16">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-highlight/10 border border-highlight/30 text-highlight text-xs font-semibold tracking-widest uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-highlight animate-pulse" />
                  Your Travel Profile
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
                  Track Your{" "}
                  <span className="text-highlight">Adventures</span>
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-light">
                  Record every trip, watch your world map fill up, and explore your travel stats.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-8 max-w-5xl mx-auto">
                {[
                  {
                    icon: Globe,
                    title: "Complete Your Map",
                    desc: "Add places you've visited and watch countries light up on your personal world map.",
                  },
                  {
                    icon: MapPin,
                    title: "Travel Records",
                    desc: "Log every trip with dates, ratings, and notes. Your travel diary, all in one place.",
                  },
                  {
                    icon: BarChart3,
                    title: "Your Stats",
                    desc: "See your travel patterns: favourite climates, trip styles, countries explored, and more.",
                  },
                ].map((feature) => (
                  <div
                    key={feature.title}
                    className="group relative p-7 rounded-2xl bg-card border border-border hover:border-accent/50 hover:shadow-soft hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center border border-accent/20 mb-5 group-hover:bg-accent/15 transition-all duration-300">
                      <feature.icon className="h-7 w-7 text-accent" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA band */}
      <section
        className="relative bg-primary py-16 sm:py-20 overflow-hidden isolate"
        style={{ contentVisibility: "auto", containIntrinsicSize: "420px" }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-accent/10 blur-3xl" />
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-accent/8 blur-2xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary-foreground tracking-tight">
            Ready to find your next trip?
          </h2>
          <p className="text-primary-foreground/85 text-base sm:text-lg max-w-md mx-auto font-light">
            Skip the overwhelm. Find your next trip in 2 minutes.
          </p>
          <Button
            variant="hero"
            size="lg"
            onClick={() => navigate('/quiz')}
            className="text-base sm:text-lg px-10 sm:px-14 py-5 sm:py-7 h-auto group"
          >
            Get Started
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Landing;
